import type * as React from "react";
import { DayPicker, type DropdownProps } from "react-day-picker";
import "react-day-picker/style.css";
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTriggerBare, SelectValue } from "./select";
import { cn } from "../lib/utils";

type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * The month and year lists of `captionLayout="dropdown*"`, drawn with the kit Select.
 *
 * react-day-picker's own `Dropdown` is a native <select> stretched invisibly over a
 * styled chip, so the open list was the browser's, not ours: a different font, no
 * theme colours, and a second look for one control (AGENTS.md section 6). This
 * swaps in the Radix Select and keeps react-day-picker's contract: it reads only
 * `e.target.value` from the change event, so a minimal event object is enough.
 *
 * The Select list portals to the body at z-[210], above the Popover (z-[120]) that
 * a `DateTimePicker` opens the calendar in, and React portal events still bubble
 * through that popover, so picking a month does not count as a click outside it.
 */
function CalendarDropdown({ options, value, onChange, disabled, className, ...rest }: DropdownProps) {
  return (
    <Select
      value={value === undefined ? undefined : String(value)}
      disabled={disabled}
      onValueChange={(next) => {
        onChange?.({ target: { value: next } } as React.ChangeEvent<HTMLSelectElement>);
      }}
    >
      {/* Its own data-slot, not "select-trigger": the month and year chips are a
          caption, and an app that dresses every form select as a field (the CRM's
          sunken pills) must not turn them into input boxes. */}
      <SelectTriggerBare
        data-slot="calendar-dropdown-trigger"
        aria-label={rest["aria-label"]}
        className={cn(
          "inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-sm font-semibold cursor-pointer",
          "hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
          "disabled:cursor-default disabled:opacity-50",
          className,
        )}
      >
        <SelectValue />
      </SelectTriggerBare>
      <SelectContent className="max-h-72 min-w-[6rem]">
        {options?.map((o) => (
          <SelectItem key={o.value} value={String(o.value)} disabled={o.disabled}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Calendar primitive (react-day-picker v9). Wrapped so we can apply the
 * shadcn/tailwind look - uses tokens from packages/tokens so it inherits
 * the same palette across web_admin, web_client and the B2B portal.
 *
 * react-day-picker v9 colours its built-in nav chevrons with the default
 * `--rdp-accent-color` (bright blue) and its hit-area sizing fought our
 * absolutely-positioned nav. We override the `Chevron` component with lucide
 * icons (theme `currentColor`) and give the nav buttons an explicit, fully
 * clickable box so the prev/next arrows behave like normal buttons.
 */
export function Calendar({ className, classNames, components, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-2", className)}
      components={{
        // rdp's own `rdp-chevron` class is deliberately not forwarded. Its
        // vendor rule is unlayered, so its `fill: var(--rdp-accent-color)` beats
        // every Tailwind utility and filled the open lucide path solid blue.
        Chevron: ({ orientation }) => {
          const Icon =
            orientation === "left"  ? ChevronLeft  :
            orientation === "right" ? ChevronRight :
            orientation === "up"    ? ChevronUp    : ChevronDown;
          return <Icon className="size-4 text-foreground" />;
        },
        Dropdown: CalendarDropdown,
        ...components,
      }}
      classNames={{
        months: "flex flex-col sm:flex-row gap-4",
        month:  "space-y-3",
        month_caption: "flex justify-center pt-1 relative items-center text-sm font-semibold",
        // pointer-events-none so the centred month label never swallows clicks
        // meant for the absolutely-positioned prev/next buttons overlapping it.
        caption_label:
          "text-sm font-semibold pointer-events-none inline-flex items-center gap-0.5",
        nav: "absolute right-2 top-1 z-10 inline-flex items-center gap-1",
        // ── Dropdown caption (only drawn under `captionLayout="dropdown*"`) ──
        //
        // Inert for every caller that stays on the default label caption, which
        // is nearly all of them. The two lists themselves are `CalendarDropdown`
        // above; this is only the row that holds them.
        //
        // The right padding is not cosmetic: `nav` above is absolutely
        // positioned at the top right of the same row, and a centred month +
        // year pair grows into it. Padding the group shifts its centre left,
        // clear of the arrows.
        dropdowns: "inline-flex items-center gap-1 pr-14",
        button_previous: "size-7 rounded-md text-foreground hover:bg-accent inline-flex items-center justify-center cursor-pointer disabled:opacity-40 disabled:cursor-default",
        button_next:     "size-7 rounded-md text-foreground hover:bg-accent inline-flex items-center justify-center cursor-pointer disabled:opacity-40 disabled:cursor-default",
        month_grid: "w-full border-collapse",
        weekdays:   "flex",
        weekday:    "text-muted-foreground rounded-md w-9 font-normal text-[0.78rem]",
        week:       "flex w-full mt-1",
        day:        "size-9 p-0 text-center text-sm relative",
        // The hover paint for an unselected day only. A selected day keeps its
        // fill on hover through the `selected` key below: react-day-picker puts
        // aria-selected on the td, never on this button, so `aria-selected:`
        // utilities here could not match.
        day_button:
          "size-9 p-0 inline-flex items-center justify-center rounded-md transition-colors " +
          "hover:bg-accent hover:text-foreground " +
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        // Today gets a soft accent ring + primary text (rounded), so the
        // wrapper TD background does NOT bleed through as a square.
        today:    "font-bold text-primary [&_button]:ring-2 [&_button]:ring-primary/40 [&_button]:rounded-md",
        // Selected must be rounded too - the TD itself picks up `bg-primary`
        // without rounding by default and we saw a square corner artefact.
        selected: "rounded-md [&_button]:rounded-md [&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary [&_button]:hover:text-primary-foreground",
        // Range selection (mode="range"): endpoints get the primary fill, the
        // span between them a soft accent band. These keys only apply in range
        // mode so they're inert for single/multiple pickers.
        //
        // A middle day is ALSO `selected` (react-day-picker gives it both keys),
        // so the `selected` button colours reach it too. Same specificity, and
        // Tailwind breaks the tie by class name, which put
        // `text-primary-foreground` after `text-foreground`: white text on the
        // light accent band. The `!` makes the band's colours win on purpose,
        // and it also keeps the `selected` hover fill off the band.
        range_start:  "rounded-l-md [&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary [&_button]:hover:text-primary-foreground",
        range_end:    "rounded-r-md [&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary [&_button]:hover:text-primary-foreground",
        range_middle: "bg-accent rounded-none [&_button]:bg-transparent! [&_button]:text-foreground! [&_button]:rounded-none",
        outside:  "text-muted-foreground/40",
        disabled: "text-muted-foreground/30 cursor-not-allowed",
        hidden:   "invisible",
        ...classNames,
      }}
      {...props}
    />
  );
}
